import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Search, Plus, MapPin, Calendar, Edit2, Eye,
    Loader2, Settings, X, Filter, Download,
    Link as LinkIcon, Users, User, Clock,
    TrendingUp, LayoutGrid, List, FileText
} from 'lucide-react';
import Swal from 'sweetalert2';
import { wcfInstance } from '../../axiosinstance';
import API_ENDPOINTS from '../../config/apiEndpoints';
import Loader from '../../components/Loader/Loader';
import SegmentationEdit from './SegmentationEdit';
import SubHeader from '../../components/common/SubHeader/SubHeader';

// Premium Components
import PremiumModuleWrapper from '../Task/Components/common/PremiumModuleWrapper';
import PremiumModuleCards from '../Task/Components/common/PremiumModuleCards';
import PremiumModuleTable from '../Task/Components/common/PremiumModuleTable';
import DynamicCalendar from '../../components/DynamicCalendar/DynamicCalendar';

// Utilities
import { } from '../../utils/helpers';
import usePermissions from '../Master_Settings/usePermissions';

import './SegmentationModule.css';

const robustFormatDate = (dateStr) => {
    if (!dateStr || dateStr === 'N/A' || dateStr === '-' || dateStr === 'null' || dateStr === 'undefined' || dateStr === '0') return 'N/A';

    try {
        const monthNames = { 'Jan': 0, 'Feb': 1, 'Mar': 2, 'Apr': 3, 'May': 4, 'Jun': 5, 'Jul': 6, 'Aug': 7, 'Sep': 8, 'Oct': 9, 'Nov': 10, 'Dec': 11 };
        const cleanStr = String(dateStr).trim();
        let dateObj = null;

        // 1. Handle WCF Date format: /Date(1741151000000)/
        if (cleanStr.includes('/Date(')) {
            const timestampStr = cleanStr.match(/\/Date\((\d+)\)\//);
            if (timestampStr && timestampStr[1]) {
                dateObj = new Date(parseInt(timestampStr[1], 10));
            }
        }

        // 2. Handle DD/MM/YYYY or DD-MM-YYYY
        if (!dateObj && (cleanStr.includes('/') || cleanStr.includes('-'))) {
            const parts = cleanStr.split(/[\/\-]/);
            if (parts.length === 3) {
                // Assuming DD/MM/YYYY or YYYY/MM/DD
                if (parts[0].length === 4) { // YYYY/MM/DD
                    dateObj = new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]));
                } else if (parts[2].length === 4) { // DD/MM/YYYY
                    dateObj = new Date(parseInt(parts[2]), parseInt(parts[1]) - 1, parseInt(parts[0]));
                }
            }
        }

        // 3. Fallback to standard constructor
        if (!dateObj || isNaN(dateObj.getTime())) {
            const d = new Date(cleanStr);
            if (!isNaN(d.getTime())) dateObj = d;
        }

        if (!dateObj || isNaN(dateObj.getTime())) return dateStr;

        const d = String(dateObj.getDate()).padStart(2, '0');
        const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        const m = months[dateObj.getMonth()];
        const y = dateObj.getFullYear();

        return `${d}-${m}-${y}`;
    } catch (e) {
        return dateStr;
    }
};

const SegmentationModule = () => {
    const navigate = useNavigate();
    const fromDateRef = useRef(null);
    const toDateRef = useRef(null);

    const { permissions } = usePermissions('PAG10036');
    const segmentationPerms = useMemo(() => {
        if (!permissions || !Array.isArray(permissions)) return null;
        return permissions.find(p => p.ModuleCode?.includes('PAG10036')) || {};
    }, [permissions]);

    const [segments, setSegments] = useState([]);
    const [loading, setLoading] = useState(false);
    const [pageIndex, setPageIndex] = useState(1);
    const [pageSize, setPageSize] = useState(2000); // Set default payload size to 2000
    const [totalCount, setTotalCount] = useState(0);
    const [searchQuery, setSearchQuery] = useState('');
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');
    const [fetchingCounts, setFetchingCounts] = useState({});
    const [fetchedCounts, setFetchedCounts] = useState({});
    const [viewMode, setViewMode] = useState('list'); // 'list' or 'calendar'
    const [activeScorecard, setActiveScorecard] = useState('All');

    // UI Pagination (for the 2000 records fetched from API)
    const [displayPageIndex, setDisplayPageIndex] = useState(1);
    const [displayPageSize, setDisplayPageSize] = useState(25);

    // Modal states
    const [showEditModal, setShowEditModal] = useState(false);
    const [segmentToEdit, setSegmentToEdit] = useState(null);
    const [isReadOnly, setIsReadOnly] = useState(false);

    // Advanced Filtering States
    const [advancedFilters, setAdvancedFilters] = useState({ logic: 'ANY', rows: [], allChecked: false });

    const advanceFilterFields = useMemo(() => [
        { label: 'Segment Name', value: 'CriteriaName', type: 'text' },
        { label: 'Criteria Type', value: 'CriteriaType', type: 'select', options: ['Lead', 'Enquiry', 'Conversion', 'All'] },
        { label: 'Status', value: 'IsActive', type: 'select', options: ['True', 'False'] },
        { label: 'Created By', value: 'CreatedByName', type: 'text' },
        { label: 'Created Date', value: 'CreatedOn', type: 'date' }
    ], []);

    const userId = localStorage.getItem('userId') || '34594';
    const API_BASE = (process.env.REACT_APP_WCF_API_BASE_URL || 'https://wcfkit19.azurewebsites.net').replace(/\/$/, '');
    const NEWV3_BASE_URL = (process.env.REACT_APP_SERVICES_API_BASE_URL || '').replace(/\/$/, '');
    const Toggle_Change = `${NEWV3_BASE_URL}/${API_ENDPOINTS.SEGMENTATION.TOGGLE_CHANGE.replace(/^\/+/, '')}`.trim();
    const LEAD_COUNT = `${NEWV3_BASE_URL}/${API_ENDPOINTS.SEGMENTATION.GET_LEAD_COUNT.replace(/^\/+/, '')}`.trim();
    const Get_Column_List = `${API_BASE}${API_ENDPOINTS.SEGMENTATION.GET_FIELD_NAME_LIST}`.trim();

    const fetchSegments = async (silent = false) => {
        if (!silent) setLoading(true);
        try {
            const Get_Segmentation_list = `${NEWV3_BASE_URL}/${API_ENDPOINTS.SEGMENTATION.GET_SEGMENTATION_LIST.replace(/^\/+/, '')}`.trim();
            const payload = {
                Token: "-2295521862261168",
                Details: {
                    UserId: parseInt(userId, 10),
                    CriteriaName: searchQuery || "",
                    FromDate: fromDate || "",
                    ToDate: toDate || "",
                    IsActive: "",
                    PageIndex: pageIndex || 1,
                    PageSize: pageSize || 2000
                }
            };

            const response = await wcfInstance.post(Get_Segmentation_list, payload);
            console.log("fetchSegments response:", response);
            if (response.data && (response.data.Details || response.data.d || Array.isArray(response.data))) {
                let data = response.data.Details || response.data.d || response.data;
                if (typeof data === 'string') {
                    try {
                        data = JSON.parse(data);
                    } catch (e) {
                        data = {};
                    }
                }
                console.log("fetchSegments data after parsing:", data);
                const tableData = data.Items || data.Table || data.Data || data.data || (Array.isArray(data) ? data : []);
                console.log("fetchSegments tableData:", tableData);
                setSegments(tableData);
                const countFromData = data.TotalCount || data.TotalRecords || (data.Table1?.[0]?.TotalCount) || (tableData.length > 0 ? tableData[0].TotalCount : 0) || tableData.length;
                setTotalCount(countFromData || 0);
            }
        } catch (error) {
            console.error('Error fetching segments:', error);
        } finally {
            if (!silent) setLoading(false);
        }
    };

    const handleStatusToggle = async (segment) => {
        const criteriaId = segment.ID || segment.Id || segment.strCriteriaID;
        const currentActive = String(segment.IsActive || segment.isActive || "").toLowerCase() === 'true' || segment.Status === 1;
        const newActive = !currentActive;

        setLoading(true);
        try {
            const payload = {
                Token: "-2295521862261168",
                Details: {
                    Mode: "status",
                    CriteriaID: parseInt(criteriaId, 10),
                    IsActive: newActive
                }
            };
            const response = await wcfInstance.post(Toggle_Change, payload);
            if (response.data && (response.data.Status === 1 || response.data.Status === '1')) {
                setSegments(prev => prev.map(s => {
                    const sId = s.ID || s.Id || s.strCriteriaID;
                    if (String(sId) === String(criteriaId)) {
                        return { ...s, IsActive: newActive ? "True" : "False", Status: newActive ? 1 : 0 };
                    }
                    return s;
                }));
                setLoading(false);
                await Swal.fire({
                    icon: 'success',
                    title: 'Success',
                    text: 'Status updated successfully',
                    confirmButtonColor: '#28a745',
                });
                fetchSegments(true);
            } else {
                Swal.fire({ icon: 'error', title: 'Error', text: response.data?.Message || 'Failed to update status.' });
            }
        } catch (error) {
            console.error('Error toggling status:', error);
            Swal.fire({ icon: 'error', title: 'Error', text: 'Failed to update status.' });
        } finally {
            setLoading(false);
        }
    };

    const handleFetchCount = async (segment) => {
        const criteriaId = segment.ID || segment.Id || segment.strCriteriaID;
        const type = segment.CriteriaType || segment.criteria || "All";

        setFetchingCounts(prev => ({ ...prev, [criteriaId]: true }));
        try {
            const payload = {
                Token: "-2295521862261168",
                Details: {
                    ParentId: parseInt(userId, 10),
                    UserId: parseInt(userId, 10)
                }
            };
            const response = await wcfInstance.post(LEAD_COUNT, payload);
            let countResult = 0;
            if (response.data && response.data.d !== undefined) {
                const dataD = response.data.d;
                if (typeof dataD === 'string') {
                    // Try parsing as JSON first
                    try {
                        const parsed = JSON.parse(dataD);
                        countResult = typeof parsed === 'number' ? parsed : (parsed.Count || parsed.TotalCount || 0);
                    } catch (e) {
                        // If it's HTML like "<p><b>Lead:</b> 308</p>", extract the number
                        // Strip HTML tags first to avoid matching numbers in class names (like mb-0 or font14)
                        const cleanText = dataD.replace(/<[^>]*>/g, '');
                        const match = cleanText.match(/(\d+)/);
                        if (match) {
                            countResult = parseInt(match[1], 10);
                        } else {
                            countResult = 0;
                        }
                    }
                } else {
                    countResult = dataD.Count || dataD.TotalCount || Number(dataD) || 0;
                }
            }
            setFetchedCounts(prev => ({ ...prev, [criteriaId]: countResult }));
        } catch (error) {
            console.error('Error fetching lead count:', error);
        } finally {
            setFetchingCounts(prev => ({ ...prev, [criteriaId]: false }));
        }
    };

    const handleEditClick = (segment, readOnly = false) => {
        setSegmentToEdit(segment);
        setIsReadOnly(readOnly);
        setShowEditModal(true);
    };

    const handleAdvanceFilterSubmit = (data) => {
        setAdvancedFilters(data);
    };

    const matchesAdvancedFilters = (item) => {
        if (!advancedFilters || !advancedFilters.rows || advancedFilters.rows.length === 0) return true;

        const { logic, rows } = advancedFilters;
        const results = rows.map(row => {
            if (!row.field || !row.value) return true;

            const rawValue = item[row.field];
            const fieldValue = String(rawValue || "").toLowerCase();
            const searchValue = String(row.value || "").toLowerCase();

            switch (row.operator) {
                case 'equals': return fieldValue === searchValue;
                case 'not_equal': return fieldValue !== searchValue;
                case 'contains': return fieldValue.includes(searchValue);
                case 'not_contains': return !fieldValue.includes(searchValue);
                case 'starts_with': return fieldValue.startsWith(searchValue);
                case 'ends_with': return fieldValue.endsWith(searchValue);
                default: return true;
            }
        });

        if (logic === 'ANY') return results.some(r => r);
        return results.every(r => r);
    };

    useEffect(() => {
        fetchSegments();
    }, [pageIndex, pageSize, userId]);

    // 1. Base filter: filtered by search and dates only (used for scorecard counts)
    const baseFilteredSegments = useMemo(() => {
        if (!Array.isArray(segments)) return [];
        return segments.filter(segment => {
            const name = (segment.CriteriaName || segment.SearchName || "").toLowerCase();
            const createdOnStr = segment.CreatedOn || "";
            const matchesSearch = name.includes(searchQuery.toLowerCase());

            // Date filter logic
            let matchesDate = true;
            if (createdOnStr) {
                const segDate = new Date(createdOnStr);
                segDate.setHours(0, 0, 0, 0);
                if (fromDate) {
                    const fDate = new Date(fromDate);
                    fDate.setHours(0, 0, 0, 0);
                    if (segDate < fDate) matchesDate = false;
                }
                if (toDate) {
                    const tDate = new Date(toDate);
                    tDate.setHours(0, 0, 0, 0);
                    if (segDate > tDate) matchesDate = false;
                }
            }

            // Advanced filters logic
            const passesAdvanced = matchesAdvancedFilters(segment);

            return matchesSearch && matchesDate && passesAdvanced;
        });
    }, [segments, searchQuery, fromDate, toDate, advancedFilters]);

    // 2. Scorecard stats - Stable counts (Total, Active, Inactive)
    const statsData = useMemo(() => {
        if (!Array.isArray(segments)) return [];
        
        const active = segments.filter(s => String(s.IsActive || s.isActive).toLowerCase() === 'true' || s.Status === 1).length;
        const inactive = segments.length - active;

        return [
            {
                title: baseFilteredSegments.length,
                subtitle: 'Total Criteria',
                icon: 'ri:file-list-3-line',
                color: 'primary',
                key: 'All'
            },
            {
                title: active,
                subtitle: 'Active Criteria',
                icon: 'ri:checkbox-circle-line',
                color: 'success',
                key: 'Active'
            },
            {
                title: inactive,
                subtitle: 'Inactive Criteria',
                icon: 'ri:error-warning-line',
                color: 'error',
                key: 'Inactive'
            }
        ];
    }, [segments]);

    // 3. Final display filter: applies scorecard selection to base results
    const filteredSegments = useMemo(() => {
        return baseFilteredSegments.filter(segment => {
            const isActive = String(segment.IsActive || segment.isActive || "").toLowerCase() === 'true' || segment.Status === 1;

            if (activeScorecard === 'All') return true;
            if (activeScorecard === 'Active') return isActive;
            if (activeScorecard === 'Inactive') return !isActive;
            return true;
        });
    }, [baseFilteredSegments, activeScorecard]);

    // 4. UI Display Pagination: Slices the filtered set into manageable chunks
    const displaySegments = useMemo(() => {
        const start = (displayPageIndex - 1) * displayPageSize;
        return filteredSegments.slice(start, start + displayPageSize);
    }, [filteredSegments, displayPageIndex, displayPageSize]);

    // Reset UI page when filters change
    useEffect(() => {
        setDisplayPageIndex(1);
    }, [searchQuery, fromDate, toDate, activeScorecard]);

    // Table Columns
    const columns = useMemo(() => [
        {
            header: 'Search Name',
            accessorKey: 'CriteriaName',
            cell: ({ row }) => {
                const item = row.original;
                const name = item.CriteriaName || item.SearchName || "Untitled";
                const criteriaType = String(item.CriteriaType || "").toUpperCase();

                const handleNameClick = () => {
                    if (criteriaType === 'LEAD' || criteriaType === 'ALL') {
                        // Navigate to Leads page with searchName in URL
                        navigate(`/leads?searchName=${encodeURIComponent(name)}`);
                    } else if (criteriaType === 'ENQUIRY') {
                        // Navigate to Enquiries page with CustomSearchId and filter=Open
                        const criteriaId = item.ID || item.Id || item.strCriteriaID;
                        navigate(`/enquiries?CustomSearchId=${criteriaId}&SearchName=${encodeURIComponent(name)}`);
                    } else {
                        // Fallback to existing edit modal behavior
                        handleEditClick(item);
                    }
                };

                return (
                    <div className="ph-name-cell">
                        <div className="ph-name-info" style={{ maxWidth: '400px' }}>
                            <span
                                className="ph-primary-link"
                                onClick={handleNameClick}
                                style={{
                                    display: '-webkit-box',
                                    WebkitLineClamp: 1,
                                    WebkitBoxOrient: 'vertical',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    fontWeight: 700,
                                    color: '#2563eb'
                                }}
                            >
                                {name}
                            </span>
                        </div>
                    </div>
                );
            }
        },
        {
            header: 'Created On',
            accessorKey: 'CreatedOn',
            cell: ({ getValue }) => <span className="ph-date-text">{robustFormatDate(getValue())}</span>
        },
        {
            header: 'Criteria',
            accessorKey: 'CriteriaType',
            cell: ({ getValue }) => {
                const type = getValue() || 'All';
                return (
                    <span className={`ph-criteria-badge is-${type.toLowerCase()}`}>
                        {type}
                    </span>
                );
            }
        },
        {
            header: 'Created By',
            accessorKey: 'CreatedByName',
            cell: ({ row }) => {
                const item = row.original;
                const creator = item.CreatedByName || item.CreatedBy || "System";
                return (
                    <div className="ph-creator-cell">
                        <div className="ph-creator-avatar">
                            <User size={14} />
                        </div>
                        <span>{creator}</span>
                    </div>
                );
            }
        },
        {
            header: 'Status',
            accessorKey: 'Status',
            cell: ({ row }) => {
                const item = row.original;
                const isActive = String(item.IsActive || item.isActive || "").toLowerCase() === 'true' || item.Status === 1;
                return (
                    <div 
                        className={`ph-status-toggle ${isActive ? 'active' : ''}`} 
                        onClick={() => segmentationPerms?.Edit !== false ? handleStatusToggle(item) : null}
                        style={{ opacity: segmentationPerms?.Edit !== false ? 1 : 0.5, cursor: segmentationPerms?.Edit !== false ? 'pointer' : 'not-allowed' }}
                    >
                        <div className="ph-toggle-track">
                            <div className="ph-toggle-thumb"></div>
                        </div>
                        <span className="ph-status-label">{isActive ? 'Active' : 'Inactive'}</span>
                    </div>
                );
            }
        },
        {
            header: 'Count',
            accessorKey: 'LeadsCount',
            cell: ({ row }) => {
                const item = row.original;
                const id = item.ID || item.Id || item.strCriteriaID;
                const type = item.CriteriaType || 'All';
                const isActive = String(item.IsActive || item.isActive || "").toLowerCase() === 'true' || item.Status === 1;

                if (!isActive) return <span className="text-muted">--</span>;

                return (
                    <div className="ph-count-cell">
                        {fetchingCounts[id] ? (
                            <Loader2 size={16} className="animate-spin text-blue-500" />
                        ) : (
                            <Eye
                                size={18}
                                className="ph-eye-icon"
                                onClick={() => handleFetchCount(item)}
                                title="Fetch Lead Count"
                            />
                        )}
                        {fetchedCounts[id] !== undefined && (
                            <span className="ph-count-badge">
                                {type}: <strong>{fetchedCounts[id]}</strong>
                            </span>
                        )}
                    </div>
                );
            }
        },
        {
            header: 'Action',
            id: 'actions',
            cell: ({ row }) => (
                <div className="ph-actions">
                    {segmentationPerms?.Edit !== false && (
                        <button className="ph-action-btn edit" onClick={() => handleEditClick(row.original, false)} title="Edit">
                            <Edit2 size={16} />
                        </button>
                    )}
                </div>
            )
        }
    ], [fetchingCounts, fetchedCounts, navigate, handleStatusToggle, segmentationPerms]);

    // Calendar Handlers
    const handleEventClick = (event) => {
        handleEditClick(event.originalData, true);
    };

    const renderCalendarEvent = (event) => {
        const item = event.originalData;
        const type = item.CriteriaType || 'All';
        return (
            <div className="ph-calendar-card segment-card" onClick={() => handleEventClick(event)}>
                <div className="ph-card-header">
                    <span className={`ph-criteria-badge is-${type.toLowerCase()}`}>{type}</span>
                    {segmentationPerms?.Edit !== false && (
                        <button className="ph-card-edit" onClick={(e) => { e.stopPropagation(); handleEditClick(item, false); }}>
                            <Edit2 size={12} />
                        </button>
                    )}
                </div>
                <div className="ph-card-body">
                    <h4 className="ph-card-title">{item.CriteriaName || item.SearchName}</h4>
                    <div className="ph-card-meta">
                        <User size={12} />
                        <span>{item.CreatedByName || "System"}</span>
                    </div>
                </div>
            </div>
        );
    };

    const criteriaLegend = (
        <div className="ph-calendar-legend">
            <div className="ph-legend-item">
                <span className="ph-legend-dot is-lead"></span>
                <span>Lead</span>
            </div>
            <div className="ph-legend-item">
                <span className="ph-legend-dot is-enquiry"></span>
                <span>Enquiry</span>
            </div>
            <div className="ph-legend-item">
                <span className="ph-legend-dot is-conversion"></span>
                <span>Conversion</span>
            </div>
            <div className="ph-legend-item">
                <span className="ph-legend-dot is-all"></span>
                <span>All</span>
            </div>
        </div>
    );

    return (
        <div className="segmentation-modern-layout flex flex-col h-screen overflow-hidden">
            {loading && <Loader message="Updating design system..." />}

            {/* Standardized Header Integration */}
            <SubHeader
                title="Segmentation"
                pageName="Segmentation"
                pageCode="PAG10036"
                searchQuery={searchQuery}
                setSearchQuery={setSearchQuery}
                totalCount={filteredSegments.length}
                searchPlaceholder="Search criteria..."
                viewMode={viewMode === 'list' ? 'table' : viewMode}
                setViewMode={(v) => setViewMode(v === 'table' ? 'list' : v)}
                showGrid={false}
                showCalendar={true}
                showSearch={!!segmentationPerms?.Search}
                showFilter={!!segmentationPerms?.Search}
                showSettings={true}
                advanceFilterFields={advanceFilterFields}
                onAdvanceFilterSubmit={segmentationPerms?.Search ? handleAdvanceFilterSubmit : null}
                onAddClick={segmentationPerms?.Add ? () => {
                    setSegmentToEdit(null);
                    setShowEditModal(true);
                } : null}
            >
                <div className="ph-date-picker-group">
                    <div className="ph-date-input" onClick={() => fromDateRef.current?.showPicker?.()}>
                        <input
                            ref={fromDateRef}
                            type="date"
                            value={fromDate}
                            onChange={(e) => setFromDate(e.target.value)}
                            className="ph-native-date"
                        />
                        <span className="ph-date-placeholder">{fromDate ? robustFormatDate(fromDate) : 'From'}</span>
                        <Calendar size={16} />
                    </div>
                    <div className="ph-date-input" onClick={() => toDateRef.current?.showPicker?.()}>
                        <input
                            ref={toDateRef}
                            type="date"
                            value={toDate}
                            onChange={(e) => setToDate(e.target.value)}
                            className="ph-native-date"
                        />
                        <span className="ph-date-placeholder">{toDate ? robustFormatDate(toDate) : 'To'}</span>
                        <Calendar size={16} />
                    </div>
                </div>
            </SubHeader>

            <div id="help-target-data-table" className="flex-1 overflow-hidden">
                <PremiumModuleWrapper
                    moduleName="Segmentation"
                loading={loading}
                onStatusClick={(s) => {
                    debugger;
                    if (s === 'ResetAll') {
                        setActiveScorecard('All');
                        setSearchQuery('');
                        setAdvancedFilters({ logic: 'ANY', rows: [], allChecked: false });
                        setFromDate('');
                        setToDate('');
                        setDisplayPageIndex(1);
                        return;
                    }
                    setActiveScorecard(s);
                }}
                customCards={statsData}
                data={displaySegments} // Pass paginated data
                columns={columns}
                totalRecords={filteredSegments.length} // Reflect full filtered count
                currentPage={displayPageIndex}
                itemsPerPage={displayPageSize}
                onPageChange={setDisplayPageIndex}
                showCards={viewMode !== 'calendar'}
                showTable={viewMode === 'list'}
                activeFilters={[activeScorecard]}
                onItemsPerPageChange={setDisplayPageSize}
                globalFilter={searchQuery}
            >
                {viewMode === 'calendar' && (
                    <div className="ph-calendar-wrapper">
                        <DynamicCalendar
                            initialView="year"
                            headerLegend={criteriaLegend}
                            events={filteredSegments.map(s => ({
                                id: s.AutoId || s.ID || s.Id || s.strCriteriaID,
                                title: s.CriteriaName || s.SearchName,
                                date: robustFormatDate(s.CreatedOn),
                                status: 'is-' + (s.CriteriaType || 'all').toLowerCase(),
                                originalData: s
                            }))}
                            statusConfig={{
                                'is-lead': '#3b82f6',
                                'is-enquiry': '#10b981',
                                'is-conversion': '#f43f5e',
                                'is-all': '#f59e0b'
                            }}
                            entityLabel="Criteria"
                            onEventClick={handleEventClick}
                            eventRenderer={renderCalendarEvent}
                        />
                    </div>
                )}
                </PremiumModuleWrapper>
            </div>

            {showEditModal && (
                <SegmentationEdit
                    initialData={segmentToEdit}
                    readOnly={isReadOnly}
                    onClose={(shouldRefresh) => {
                        setShowEditModal(false);
                        if (shouldRefresh === true) {
                            fetchSegments(true);
                        }
                    }}
                />
            )}
        </div>
    );
};

export default SegmentationModule;
